import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import axios from '~/data/http/axios'
import ElementPicker from './ElementPicker'
// import MarketPlace from '~/Common-Objects/TemplateEditor/MarketPlace'
import i18n from '~/i18n'
import RightTools from '~/Common-Objects/TemplateEditor/RightTools'
import ChecksTools from './Checks'
import AiRefine from './AiRefine'
import PanelHeader from '~/Common-Objects/TemplateEditor/PanelHeader'
import Expander from '~/Common-Objects/Expander'
import LeftToolsNavigation from './navigation'
import EmptyState from '~/Common-Objects/EmptyState'
import useTierAllowed from '~/Common-Objects/hooks/useTierAllowed'
import { cn } from '~/utils/cn'

const DragDrop = (props) => {
    const [currentTool, setCurrentTool] = useState(0)
    const [prevSelectedPath, setPrevSelectedPath] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [components, setComponents] = useState(props.components)
    const [layouts, setLayouts] = useState(props.layouts)
    const [blocks, setBlocks] = useState(props.blocks)
    const [userBlocks, setUserBlocks] = useState(props.userBlocks)
    const [marketplaceBlocks, setMarketplaceBlocks] = useState(props.marketPlaceComponents)
    const [showUpsell, setShowUpsell] = useState(false)
    const [upsellLabel, setUpsellLabel] = useState('')
    const [userApps, setUserApps] = useState([])
    const [expandedStyleSections, setExpandedStyleSections] = useState([])
    const [expandedElementSections, setExpandedElementSections] = useState(null)
    const [globalStyleFocus, setGlobalStyleFocus] = useState(null)
    // Add (0) and Styles (1) share one 335px slot. Both stay mounted so we can
    // fade between them like an iPhone overlay (~20px of drift), not a carousel.
    const [trackAtStyles, setTrackAtStyles] = useState(false)
    const [trackMoving, setTrackMoving] = useState(false)
    const [trackDirection, setTrackDirection] = useState('forward')
    // One-frame flag: park the incoming panel off to the side with transitions
    // off, then clear it so CSS can ease that panel onto the outgoing one.
    const [trackEntering, setTrackEntering] = useState(false)
    // Checks (2), AI (3), and the element inspector (10) sit on top of Add/Styles
    // and fade out when you leave them, instead of overlaying against each other.
    const [snapTool, setSnapTool] = useState(null)
    const [snapExiting, setSnapExiting] = useState(false)
    const prevToolRef = useRef(0)
    // Done deselects immediately. Keep the last selected element so the exiting
    // inspector does not remount as the Styles editor (that flash of a spinner).
    const inspectorSelectionRef = useRef(props.selectedElement)
    if (props.selectedElement?.isSelected) {
        inspectorSelectionRef.current = props.selectedElement
    }
    const customTemplateBlockCheck = useTierAllowed('customTemplateBlocks')
    const hasAiTemplateBuilder = Boolean(props.accountMaster?.accountMaster?.beta?.aiTemplateBuilder)
    const showAiTab = hasAiTemplateBuilder && (props.siteMaster?.siteId == 1 || props.siteMaster?.aiEnabled === true)

    useEffect(() => {
        if (!customTemplateBlockCheck.tierAllowed) {
            setShowUpsell(true)
            setUpsellLabel(customTemplateBlockCheck.requiredTier?.name || 'Growth')
        } else {
            setShowUpsell(false)
            setUpsellLabel('')
        }
    }, [customTemplateBlockCheck.tierAllowed, customTemplateBlockCheck.requiredTier])

    useEffect(() => {
        const selectionChanged = prevSelectedPath !== props.selectedElement.path

        if (!props.selectedElement.isSelected) {
            if (currentTool === 10 && selectionChanged) {
                setCurrentTool(0)
            }
        } else {
            if (selectionChanged) {
                setCurrentTool(10)
                setExpandedElementSections(null)
            }
        }

        if (selectionChanged) {
            setPrevSelectedPath(props.selectedElement.path)
        }
    }, [props.selectedElement.isSelected, props.selectedElement.path, prevSelectedPath, currentTool])

    const handleToolChange = (tool) => {
        setCurrentTool(tool)
    }

    const openGlobalStyle = ({ category, subCategory }) => {
        setGlobalStyleFocus({ category, subCategory })
        setExpandedStyleSections(category ? [category] : [])
        setCurrentTool(1)
    }

    useEffect(() => {
        setSearchTerm('')
        if (currentTool === 0 || currentTool === 1 || currentTool === 3) {
            props.deselectElement()
        }
    }, [currentTool])

    // Overlay / snap classes. Depend only on currentTool — including
    // trackAtStyles here used to drop --moving on the same frame we started it.
    useEffect(() => {
        const prev = prevToolRef.current
        prevToolRef.current = currentTool

        const onRail = currentTool === 0 || currentTool === 1
        const fromRail = prev === 0 || prev === 1
        const fromSnap = prev === 2 || prev === 3 || prev === 10
        const prefersReduced =
            typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (currentTool === 2 || currentTool === 3 || currentTool === 10) {
            setSnapTool(currentTool)
            setSnapExiting(false)
            setTrackMoving(false)
            setTrackEntering(false)
            return
        }

        if (!onRail) return

        const nextAtStyles = currentTool === 1

        if (prefersReduced) {
            setTrackDirection(nextAtStyles ? 'forward' : 'back')
            setTrackAtStyles(nextAtStyles)
            setTrackMoving(false)
            setTrackEntering(false)
            setSnapTool(null)
            setSnapExiting(false)
            return
        }

        // Leave Checks / AI / inspector: keep that panel mounted so it can fade
        // out, and overlay Add or Styles underneath it.
        if (fromSnap) {
            setTrackDirection(nextAtStyles ? 'forward' : 'back')
            setTrackAtStyles(nextAtStyles)
            setTrackMoving(true)
            setTrackEntering(true)
            setSnapExiting(true)
            const frame = requestAnimationFrame(() => {
                setTrackEntering(false)
            })
            return () => cancelAnimationFrame(frame)
        }

        if (nextAtStyles === trackAtStyles) {
            setTrackMoving(false)
            return
        }

        if (!fromRail) {
            setTrackDirection(nextAtStyles ? 'forward' : 'back')
            setTrackAtStyles(nextAtStyles)
            setTrackMoving(false)
            return
        }

        // Add ↔ Styles: this frame parks the incoming panel 20px off with
        // transition:none. Next frame we flip trackAtStyles so --active eases it in.
        setTrackDirection(nextAtStyles ? 'forward' : 'back')
        setTrackMoving(true)
        const frame = requestAnimationFrame(() => {
            setTrackAtStyles(nextAtStyles)
        })
        return () => cancelAnimationFrame(frame)
    }, [currentTool])

    const onTrackTransitionEnd = (event) => {
        if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return
        setTrackMoving(false)
        setTrackEntering(false)
    }

    const onSnapTransitionEnd = (event) => {
        if (event.propertyName !== 'opacity') return
        if (!snapExiting) return
        setSnapTool(null)
        setSnapExiting(false)
    }

    useEffect(() => {
        const filteredComponents = props.components.filter((component) => {
            if (searchTerm) {
                if (!component.label?.toLowerCase().includes(searchTerm.toLowerCase())) {
                    return false
                }
            }
            if (component.marketPlace) {
                return false
            }
            return true
        })

        setComponents(filteredComponents)
        const filteredLayouts = props.layouts.filter((layout) => {
            if (searchTerm) {
                if (!layout.label?.toLowerCase().includes(searchTerm.toLowerCase())) {
                    return false
                }
            }
            if (layout.hidden) {
                return false
            }
            return true
        })
        setLayouts(filteredLayouts)
        const filteredBlocks = props.blocks.filter((block) => {
            if (searchTerm) {
                if (!block.label?.toLowerCase().includes(searchTerm.toLowerCase())) {
                    return false
                }
            }
            return true
        })
        setBlocks(filteredBlocks)
        const filteredUserBlocks = props.userBlocks.filter((userBlock) => {
            if (searchTerm) {
                if (!userBlock.title?.toLowerCase().includes(searchTerm.toLowerCase())) {
                    return false
                }
            }
            return true
        })
        setUserBlocks(filteredUserBlocks)
        const filteredMarketplaceBlocks = props.marketPlaceComponents.filter((marketplaceBlock) => {
            if (searchTerm) {
                if (!marketplaceBlock.name?.toLowerCase().includes(searchTerm.toLowerCase())) {
                    return false
                }
            }
            return true
        })
        setMarketplaceBlocks(filteredMarketplaceBlocks)
    }, [searchTerm, props.components, props.layouts, props.blocks, props.userBlocks, props.marketPlaceComponents])

    useEffect(() => {
        getUserApps()
    }, [])

    const getUserApps = () => {
        axios.get('/application/account').then((data) => {
            setUserApps(data.data.Applications)
        })
    }

    return (
        <div
            className='dndEditorv2-leftTools-shell'
            data-onboarding-enabled={false}
            data-onboarding-id='dragdrop_lefttools'
        >
            <LeftToolsNavigation
                currentTool={currentTool}
                setCurrentTool={handleToolChange}
                templateTypeOrig={props.templateTypeOrig}
                templateType={props.type}
                showAiTab={showAiTab}
            />
            <div className='dndEditorv2-leftTools-holder'>
                {/* Add and Styles stay stacked in this track; CSS cross-fades them. */}
                <div
                    className={cn('dndEditorv2-leftTools-track', {
                        'dndEditorv2-leftTools-track--styles': trackAtStyles,
                        'dndEditorv2-leftTools-track--moving': trackMoving,
                        'dndEditorv2-leftTools-track--forward': trackDirection === 'forward',
                        'dndEditorv2-leftTools-track--back': trackDirection === 'back',
                        'dndEditorv2-leftTools-track--hidden': currentTool !== 0 && currentTool !== 1
                    })}
                    onTransitionEnd={onTrackTransitionEnd}
                >
                    <div
                        className={cn('dndEditorv2-leftTools-panel', {
                            'dndEditorv2-leftTools-panel--active': !trackAtStyles && !trackEntering
                        })}
                    >
                        <PanelHeader
                            hideBorder={false}
                            title={i18n.t('templates:add.drag.elements.header')}
                            subtitle={
                                props.isForm
                                    ? i18n.t('templates:add.drag.elements.subHeaderForm')
                                    : i18n.t('templates:add.drag.elements.subHeaderEmail')
                            }
                            searchPlaceholder={i18n.t('templates:add.drag.elements.searchPlaceholder')}
                            onSearch={(e) => setSearchTerm(e.target.value)}
                        />
                        {components.length > 0 && (
                            <Expander
                                key={`basic-${searchTerm}`}
                                label={'Basic Blocks'}
                                expanded={searchTerm.length > 0 || searchTerm.length === 0}
                            >
                                <ElementPicker
                                    type='element'
                                    components={components}
                                    isForm={props.isForm}
                                    insertElement={props.insertElement}
                                    templateJson={props.templateJson}
                                />
                            </Expander>
                        )}
                        {props.isLandingPage && blocks.length > 0 && (
                            <Expander key={`predefined-${searchTerm}`} label={'Layouts'} expanded={true}>
                                <ElementPicker
                                    type='block'
                                    components={blocks}
                                    isForm={props.isForm}
                                    insertElement={props.insertElement}
                                />
                            </Expander>
                        )}
                        {layouts.length > 0 && (
                            <Expander key={`layout-${searchTerm}`} label={'Containers'} expanded={true}>
                                <ElementPicker
                                    type='layout'
                                    components={layouts}
                                    isForm={props.isForm}
                                    insertElement={props.insertElement}
                                />
                            </Expander>
                        )}
                        {/* {blocks.length > 0 && (
                        <Expander label={'Saved Blocks'} expanded={false}>
                            <ElementPicker
                                type='block'
                                components={blocks}
                                isForm={props.isForm}
                                insertElement={props.insertElement}
                            />
                        </Expander>
                    )} */}
                        {!props.isForm && (userBlocks.length > 0 || searchTerm.length === 0) && (
                            <Expander
                                key={`userBlock-${searchTerm}`}
                                label={'Saved Layouts'}
                                showUpsell={showUpsell}
                                upsellLabel={upsellLabel}
                                expanded={searchTerm.length > 0}
                            >
                                <ElementPicker
                                    type='userBlock'
                                    components={userBlocks}
                                    isForm={props.isForm}
                                    isLandingPage={props.isLandingPage}
                                    updateBlocks={props.updateBlocks}
                                    insertElement={props.insertElement}
                                    showUpsell={showUpsell}
                                />
                            </Expander>
                        )}
                        {marketplaceBlocks.length > 0 && (
                            <Expander key={`app-${searchTerm}`} label={'Apps'} expanded={searchTerm.length > 0}>
                                <ElementPicker
                                    type='app'
                                    components={marketplaceBlocks}
                                    userApps={userApps}
                                    isForm={props.isForm}
                                    insertElement={props.insertElement}
                                />
                            </Expander>
                        )}
                        {components.length === 0 &&
                            layouts.length === 0 &&
                            blocks.length === 0 &&
                            marketplaceBlocks.length === 0 &&
                            userBlocks.length === 0 && (
                                <EmptyState
                                    title={i18n.t('templates:add.drag.elements.no-elements')}
                                    icon={'thumbsDown'}
                                />
                            )}
                    </div>
                    <div
                        className={cn('dndEditorv2-leftTools-panel', {
                            'dndEditorv2-leftTools-panel--active': trackAtStyles && !trackEntering
                        })}
                    >
                        <RightTools
                            formLandingPage={props.formLandingPage}
                            isFormLandingPage={props.isFormLandingPage}
                            campaign={props.campaign}
                            templateId={props.templateId}
                            isForm={props.isForm}
                            saveTemplate={props.saveTemplate}
                            selectedElement={{ isSelected: false }}
                            templateJson={props.templateJson}
                            deselectElement={props.deselectElement}
                            updateElementsData={props.updateElementsData}
                            saveFaveColor={props.saveFaveColor}
                            updateBlocks={props.updateBlocks}
                            marketOpen={props.marketOpen}
                            templateType={props.type}
                            isDragging={props.isDragging}
                            expandedElements={expandedStyleSections}
                            setExpandedElements={setExpandedStyleSections}
                            isLandingPage={props.isLandingPage}
                            landingPageId={props.landingPageId}
                            openGlobalStyle={openGlobalStyle}
                            globalStyleFocus={globalStyleFocus}
                            canvasScreenType={props.canvasScreenType}
                        />
                    </div>
                </div>

                {/* Snap panels sit above the Add/Styles track and fade out on the way back. */}
                {snapTool === 2 && (
                    <div
                        className={cn('dndEditorv2-leftTools-panel dndEditorv2-leftTools-panel--snap', {
                            'dndEditorv2-leftTools-panel--exiting': snapExiting
                        })}
                        onTransitionEnd={onSnapTransitionEnd}
                    >
                        <ChecksTools
                            templateId={props.templateId}
                            type={props.type}
                            landingPage={props.landingPage}
                            templateJson={props.templateJson}
                            selectElement={props.selectElement}
                        />
                    </div>
                )}

                {snapTool === 3 && showAiTab && (
                    <div
                        className={cn('dndEditorv2-leftTools-panel dndEditorv2-leftTools-panel--snap', {
                            'dndEditorv2-leftTools-panel--exiting': snapExiting
                        })}
                        onTransitionEnd={onSnapTransitionEnd}
                    >
                        <AiRefine
                            templateId={props.templateId}
                            landingPageId={props.landingPageId}
                            isLandingPage={props.isLandingPage}
                            templateJson={props.templateJson}
                            updateElementsData={props.updateElementsData}
                            bodyTemplate={props.bodyTemplate}
                            layouts={props.layouts}
                            blocks={props.blocks}
                            components={props.components}
                        />
                    </div>
                )}

                {snapTool === 10 && (
                    <div
                        className={cn('dndEditorv2-leftTools-panel dndEditorv2-leftTools-panel--snap', {
                            'dndEditorv2-leftTools-panel--exiting': snapExiting
                        })}
                        onTransitionEnd={onSnapTransitionEnd}
                    >
                        {/* Frozen selection — live selectedElement is already cleared on Done. */}
                        <RightTools
                            isDragging={props.isDragging}
                            formLandingPage={props.formLandingPage}
                            isFormLandingPage={props.isFormLandingPage}
                            campaign={props.campaign}
                            templateId={props.templateId}
                            isForm={props.isForm}
                            isLandingPage={props.isLandingPage}
                            landingPageId={props.landingPageId}
                            saveTemplate={props.saveTemplate}
                            selectedElement={inspectorSelectionRef.current}
                            templateJson={props.templateJson}
                            deselectElement={props.deselectElement}
                            updateElementsData={props.updateElementsData}
                            saveFaveColor={props.saveFaveColor}
                            updateBlocks={props.updateBlocks}
                            marketOpen={props.marketOpen}
                            templateType={props.templateType}
                            expandedElements={expandedElementSections}
                            setExpandedElements={setExpandedElementSections}
                            openGlobalStyle={openGlobalStyle}
                            globalStyleFocus={globalStyleFocus}
                            canvasScreenType={props.canvasScreenType}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default connect((store) => {
    return { user: store.user, siteMaster: store.siteMaster, accountMaster: store.accountMaster }
})(DragDrop)
